import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getPendingTeachers,
  approveTeacher,
  rejectTeacher,
  getSchools,
  createSchool,
  updateSchool,
  deleteSchool
} from '../../services/api';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { handleRequest, loading } = useApi();
  const [pendingTeachers, setPendingTeachers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [showSchoolForm, setShowSchoolForm] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [schoolForm, setSchoolForm] = useState({
    name: '',
    address: '',
    city: '',
    country: ''
  });

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }
    
    // Check if user is admin
    if (!user) {
      console.warn('AdminDashboard: No user found');
      navigate('/dashboard');
      return;
    }
    
    // Check if role exists
    if (!user.role) {
      console.error('AdminDashboard: User role is undefined. User object:', user);
      setErrorMessage('Kontoen din har ingen rolle tildelt. Kontakt støtte, eller oppdater kontoen din i databasen.');
      return;
    }
    
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      console.warn('AdminDashboard: User role is', user.role, 'but admin/superadmin is required');
      navigate('/dashboard');
      return;
    }
    
    // Fetch pending teachers if user is admin or superadmin
    fetchPendingTeachers();
    
    // Fetch schools if user is superadmin
    if (user.role === 'superadmin') {
      fetchSchools();
    }
  }, [user, authLoading, navigate]);

  const fetchPendingTeachers = async () => {
    try {
      const response = await handleRequest(getPendingTeachers);
      setPendingTeachers(response.data || []);
    } catch (error) {
      setErrorMessage('Kunne ikke laste inn ventende lærere');
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await getSchools(''); // Empty search to get all schools
      // Handle nested response structure
      const schoolsData = response.data?.data || response.data || [];
      setSchools(Array.isArray(schoolsData) ? schoolsData : []);
    } catch (error) {
      console.error('Failed to load schools:', error);
      setSchools([]);
    }
  };

  const handleApprove = async (teacherId) => {
    setProcessingId(teacherId);
    setErrorMessage('');
    try {
      await handleRequest(approveTeacher, teacherId);
      setPendingTeachers(prev => prev.filter(t => (t._id || t.id) !== teacherId));
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke godkjenne lærer');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (teacherId) => {
    if (!window.confirm('Er du sikker på at du vil avvise denne læreren? Denne handlingen kan ikke angres.')) {
      return;
    }
    setProcessingId(teacherId);
    setErrorMessage('');
    try {
      await handleRequest(rejectTeacher, teacherId);
      setPendingTeachers(prev => prev.filter(t => (t._id || t.id) !== teacherId));
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke avvise lærer');
    } finally {
      setProcessingId(null);
    }
  };

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-dashboard-content">
          <p className="loading-message">Laster...</p>
        </div>
      </div>
    );
  }

  const handleSchoolSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchool) {
        await handleRequest(updateSchool, editingSchool._id || editingSchool.id, schoolForm);
      } else {
        await handleRequest(createSchool, schoolForm);
      }
      setShowSchoolForm(false);
      setEditingSchool(null);
      setSchoolForm({ name: '', address: '', city: '', country: '' });
      fetchSchools();
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke lagre skole');
    }
  };

  const handleDeleteSchool = async (schoolId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne skolen? Denne handlingen kan ikke angres.')) {
      return;
    }
    try {
      await handleRequest(deleteSchool, schoolId);
      fetchSchools();
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke slette skole');
    }
  };

  const handleEditSchool = (school) => {
    setEditingSchool(school);
    setSchoolForm({
      name: school.name || '',
      address: school.address || '',
      city: school.city || '',
      country: school.country || ''
    });
    setShowSchoolForm(true);
  };

  // Redirect if not admin or superadmin (this should be handled by useEffect, but just in case)
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return null;
  }

  const isSuperAdmin = user.role === 'superadmin';

  return (
    <div className="admin-dashboard-container">
      <div className="admin-dashboard-header">
        <h1>{isSuperAdmin ? 'Superadminpanel' : 'Adminpanel'}</h1>
        <button className="back-to-dashboard-btn" onClick={() => navigate('/dashboard')}>
          Tilbake til oversikten
        </button>
      </div>

      <div className="admin-dashboard-content">
        {errorMessage && (
          <ErrorCard message={errorMessage} onClose={() => setErrorMessage('')} />
        )}

        <div className="admin-section">
          <h2>Ventende lærer-godkjenninger</h2>
          {loading && pendingTeachers.length === 0 ? (
            <p className="loading-message">Laster ventende lærere...</p>
          ) : pendingTeachers.length === 0 ? (
            <div className="empty-state">
              <p>Ingen ventende lærer-godkjenninger</p>
            </div>
          ) : (
            <div className="pending-teachers-list">
              {pendingTeachers.map((teacher) => (
                <div key={teacher._id || teacher.id} className="teacher-card">
                  <div className="teacher-info">
                    <h3>{teacher.name}</h3>
                    <p className="teacher-email">{teacher.email}</p>
                    <p className="teacher-date">
                      Registrert: {new Date(teacher.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="teacher-actions">
                    <button
                      className="approve-btn"
                      onClick={() => handleApprove(teacher._id || teacher.id)}
                      disabled={processingId === (teacher._id || teacher.id)}
                    >
                      {processingId === (teacher._id || teacher.id) ? 'Behandler...' : 'Godkjenn'}
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => handleReject(teacher._id || teacher.id)}
                      disabled={processingId === (teacher._id || teacher.id)}
                    >
                      {processingId === (teacher._id || teacher.id) ? 'Behandler...' : 'Avvis'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {isSuperAdmin && (
          <div className="admin-section">
            <div className="section-header">
              <h2>Skoleadministrasjon</h2>
              <button 
                className="create-btn"
                onClick={() => {
                  setEditingSchool(null);
                  setSchoolForm({ name: '', address: '', city: '', country: '' });
                  setShowSchoolForm(true);
                }}
              >
                + Opprett skole
              </button>
            </div>

            {showSchoolForm && (
              <div className="school-form">
                <h3>{editingSchool ? 'Rediger skole' : 'Opprett ny skole'}</h3>
                <form onSubmit={handleSchoolSubmit}>
                  <div className="form-group">
                    <label>Skolenavn *</label>
                    <input
                      type="text"
                      value={schoolForm.name}
                      onChange={(e) => setSchoolForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Adresse</label>
                    <input
                      type="text"
                      value={schoolForm.address}
                      onChange={(e) => setSchoolForm(prev => ({ ...prev, address: e.target.value }))}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>By</label>
                      <input
                        type="text"
                        value={schoolForm.city}
                        onChange={(e) => setSchoolForm(prev => ({ ...prev, city: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Land</label>
                      <input
                        type="text"
                        value={schoolForm.country}
                        onChange={(e) => setSchoolForm(prev => ({ ...prev, country: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="submit-btn">
                      {editingSchool ? 'Oppdater skole' : 'Opprett skole'}
                    </button>
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => {
                        setShowSchoolForm(false);
                        setEditingSchool(null);
                        setSchoolForm({ name: '', address: '', city: '', country: '' });
                      }}
                    >
                      Avbryt
                    </button>
                  </div>
                </form>
              </div>
            )}

            {schools.length === 0 ? (
              <div className="empty-state">
                <p>Ingen skoler opprettet ennå</p>
              </div>
            ) : (
              <div className="schools-list">
                {schools.map((school) => (
                  <div key={school._id || school.id} className="school-card">
                    <div className="school-info">
                      <h3>{school.name}</h3>
                      {(school.address || school.city || school.country) && (
                        <p className="school-location">
                          {[school.address, school.city, school.country].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {school.createdAt && (
                        <p className="school-meta">
                          Opprettet: {new Date(school.createdAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="school-actions">
                      <button
                        className="edit-btn"
                        onClick={() => handleEditSchool(school)}
                      >
                        Rediger
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteSchool(school._id || school.id)}
                      >
                        Slett
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
